import { z } from "zod";
import { syncMetaSchema } from "./sync-meta.js";

/**
 * Polymorphic owner shape used by player / calendar_event / board /
 * formation / media_ref. The `ownerKind` tag controls who/what owns the
 * row; `ownerId` is the id of that owner (team.id, user.id, etc.).
 *
 * Defaults per entity (enforced by the repo layer, not the schema):
 *   • player         → owner_kind='team',  owner_id=<teamId>
 *   • calendar_event → owner_kind='coach', owner_id=<userId>
 *   • board          → owner_kind='coach', owner_id=<userId>
 *   • formation      → owner_kind='team',  owner_id=<teamId>
 */
export const ownerKindSchema = z.enum([
  "coach",
  "team",
  "player",
  "board",
  "event",
  /** Free-form files surfaced by the Mediathek browser. */
  "library",
  /** User profile picture — written by the account/profile flow. */
  "user",
]);
export type OwnerKind = z.infer<typeof ownerKindSchema>;

/** Team — belongs to one Organization. Coaches are linked via TeamCoach. */
export const teamSchema = syncMetaSchema.extend({
  name: z.string().min(1).max(120),
  league: z.string().max(120).nullable().optional(),
});
export type Team = z.infer<typeof teamSchema>;

/**
 * Many-to-many join between Team and User (coach).
 * The owner of the organization implicitly has access to every team —
 * no team_coach row is required for that. Non-owner coaches must have
 * an explicit assignment to access a team's data.
 */
export const teamCoachSchema = syncMetaSchema.extend({
  teamId: z.string().uuid(),
  userId: z.string(),
  role: z.enum(["coach", "assistant"]).default("coach"),
});
export type TeamCoach = z.infer<typeof teamCoachSchema>;

export const playerPositionSchema = z.enum([
  "GK",
  "CB",
  "LB",
  "RB",
  "DM",
  "CM",
  "AM",
  "LW",
  "RW",
  "ST",
]);
export type PlayerPosition = z.infer<typeof playerPositionSchema>;

export const footSchema = z.enum(["left", "right", "both"]);
export type Foot = z.infer<typeof footSchema>;

export const playerSchema = syncMetaSchema.extend({
  ownerKind: ownerKindSchema,
  ownerId: z.string(),
  name: z.string().min(1).max(120),
  imageRef: z.string().nullable(),
  position: playerPositionSchema.nullable(),
  foot: footSchema.nullable(),
  shirtNumber: z.number().int().min(1).max(99).nullable(),
  email: z.string().email().nullable(),
  notes: z.string().max(2000).nullable(),
  // 0-100 rating bars rendered with <StatBar />.
  stats: z
    .object({
      stamina: z.number().int().min(0).max(100).nullable(),
      gameIntelligence: z.number().int().min(0).max(100).nullable(),
      teamSpirit: z.number().int().min(0).max(100).nullable(),
      technique: z.number().int().min(0).max(100).nullable(),
    })
    .partial(),
});
export type Player = z.infer<typeof playerSchema>;

export const calendarEventTypeSchema = z.enum([
  "training",
  "match",
  "meeting",
  "medical",
  "travel",
  "other",
]);

export const calendarEventSchema = syncMetaSchema.extend({
  ownerKind: ownerKindSchema,
  ownerId: z.string(),
  title: z.string().min(1).max(200),
  description: z.string().max(4000).nullable(),
  type: calendarEventTypeSchema,
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime().nullable(),
  location: z.string().max(200).nullable(),
  /** Optional opaque id linking the event to a recurrence series. */
  seriesId: z.string().max(64).nullable().optional(),
  /** Marks the event as a whole-day entry (time fields ignored on display). */
  allDay: z.boolean().optional(),
});
export type CalendarEvent = z.infer<typeof calendarEventSchema>;

/** A formation snapshot — opaque JSON owned by the Aufstellung feature. */
export const formationSchema = syncMetaSchema.extend({
  ownerKind: ownerKindSchema,
  ownerId: z.string(),
  name: z.string().min(1).max(120),
  preset: z.string().nullable(), // e.g. "4-4-2"
  positionsJson: z.string(), // JSON-serialized Position[] (normalized 0-1 coords)
});
export type Formation = z.infer<typeof formationSchema>;

/** Tactic boards & whiteboards persist as opaque JSON snapshots — keeps the door open for a CRDT later. */
export const boardKindSchema = z.enum(["taktik", "whiteboard"]);
export const boardSchema = syncMetaSchema.extend({
  ownerKind: ownerKindSchema,
  ownerId: z.string(),
  kind: boardKindSchema,
  name: z.string().min(1).max(200),
  snapshotJson: z.string(),
});
export type Board = z.infer<typeof boardSchema>;

/** Reference to a media object stored in MinIO (or any S3-compatible bucket). */
export const mediaRefSchema = syncMetaSchema.extend({
  ownerKind: ownerKindSchema,
  ownerId: z.string(),
  s3Key: z.string(),
  mime: z.string(),
  bytes: z.number().int().nonnegative(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
});
export type MediaRef = z.infer<typeof mediaRefSchema>;
